import { useEffect, useState } from "react";

import { Pause, RefreshCw, X } from "lucide-react";

import { useCookieConsent } from "@/hooks/useCookieConsent";
import {
  activatePendingVersionUpdate,
  getPendingVersionUpdate,
  NEW_VERSION_AVAILABLE_EVENT,
} from "@/services/versionService";
import { BrandLogo } from "@/components/shared/BrandLogo";

const AUTO_REFRESH_SECONDS = 10;

export function NewVersionPrompt() {
  const { requiresConsent } = useCookieConsent();
  const [pendingVersion, setPendingVersion] = useState(() => getPendingVersionUpdate());
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(AUTO_REFRESH_SECONDS);
  const [countdownPaused, setCountdownPaused] = useState(false);

  useEffect(() => {
    function handleNewVersion(event: WindowEventMap[typeof NEW_VERSION_AVAILABLE_EVENT]) {
      setPendingVersion(event.detail.version);
      setDismissedVersion(null);
      setRefreshing(false);
      setSecondsRemaining(AUTO_REFRESH_SECONDS);
      setCountdownPaused(false);
    }

    window.addEventListener(NEW_VERSION_AVAILABLE_EVENT, handleNewVersion);
    return () => {
      window.removeEventListener(NEW_VERSION_AVAILABLE_EVENT, handleNewVersion);
    };
  }, []);

  useEffect(() => {
    if (
      !pendingVersion ||
      dismissedVersion === pendingVersion ||
      countdownPaused ||
      refreshing
    ) {
      return undefined;
    }

    if (secondsRemaining <= 0) {
      setRefreshing(true);
      activatePendingVersionUpdate();
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setSecondsRemaining((current) => Math.max(0, current - 1));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [
    countdownPaused,
    dismissedVersion,
    pendingVersion,
    refreshing,
    secondsRemaining,
  ]);

  if (!pendingVersion || dismissedVersion === pendingVersion) {
    return null;
  }

  const bottomOffset = requiresConsent
    ? "bottom-40 sm:bottom-5"
    : "bottom-3 sm:bottom-5";

  return (
    <aside
      className={[
        "fixed left-1/2 z-50 w-[min(23rem,calc(100vw-1.5rem))] -translate-x-1/2 border border-neutral-700 bg-neutral-950 p-3 text-white shadow-2xl transition-all",
        bottomOffset,
      ].join(" ")}
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-white text-neutral-950">
          <BrandLogo showWordmark={false} className="[&_svg]:h-7 [&_svg]:w-7" />
        </div>
        <div className="min-w-0 flex-1 pr-8">
          <h2 className="text-sm font-semibold">New version available</h2>
          <p className="mt-1 text-xs leading-5 text-neutral-300">
            {countdownPaused
              ? "Auto refresh is paused. Refresh when you are ready."
              : `Auto refresh in ${secondsRemaining}s for the latest update.`}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setRefreshing(true);
                activatePendingVersionUpdate();
              }}
              className="inline-flex h-8 items-center gap-2 border border-white bg-white px-3 text-xs font-medium text-neutral-950 transition hover:bg-neutral-950 hover:text-white"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
            {!countdownPaused ? (
              <button
                type="button"
                onClick={() => setCountdownPaused(true)}
                className="inline-flex h-8 items-center gap-2 border border-neutral-600 px-3 text-xs font-medium text-neutral-200 transition hover:border-white hover:text-white"
              >
                <Pause className="h-3.5 w-3.5" />
                Stop countdown
              </button>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDismissedVersion(pendingVersion)}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center border border-neutral-700 text-neutral-300 transition hover:border-white hover:text-white"
          aria-label="Dismiss new version prompt"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </aside>
  );
}
